import React, { useState, useCallback, useEffect } from "react";
import PropTypes from 'prop-types';
import { asMoney, isoDateToMMM } from "../../../utils";
import { Icon } from '@iconify/react';
import { noChallengeIcon, notPoleIcon, poleIcon, challengeableIcon, underChallengeIcon, unchallengeableIcon, pencilIcon } from "../../../utils/icons";
import {
    PanelBooking,
    TweakPanelBookingError,
    TweakPanelBookingInput,
    useCampaignDeletePanelsMutationMutation,
    useCampaignTweakPanelsMutationMutation,
    useGetCampaignPanelBookingsQueryQuery,
} from "../../../generated/graphql";
import {
    Cancel,

    ChevronLeft,
    ChevronRight, Compress,
    Delete, Expand,
    Save,
    ThumbUpSharp,
    Web,
} from "@mui/icons-material";
import { Button, ButtonGroup, Card, CardContent, FormLabel, TextField, Tooltip, Typography } from "@mui/material";
import { FieldTableColumnProps } from "../../../components/FieldTable/FieldTableTypes";
import WaitSpinner from "../../../components/wait-spinner";
import { FieldTable } from "../../../components/FieldTable/FieldTable";
import { useSnackbar } from "notistack";
import { useOutletContext } from "react-router-dom";
import { CampaignOutletContextValue } from "../form";
import { useDaybookContext } from "../../../contexts/Daybook/DayBookContext";
import { ModalWindow } from "../../../components/ModalWindow";
import { FileInput } from "../../../components/file-input";
import { useUserContext } from "../../../contexts/user-context";
import { ChallengeEmailDialog } from "../ChallengeEmail";
import challengeIcon from '@iconify/icons-mdi/sword-cross';


const CreateChallengeDialog = ({ isShown, ids, done, close }) => {
    const [files, setFiles] = React.useState([]);
    const changeFiles = (evt) => {
        setFiles(evt.target.value);
    }
    return (<ModalWindow isOpen={isShown} onClose={close} header={
        "Challenge Bookings"
    } actions={
        <>
            <Button onClick={close} startIcon={<Cancel />}> Cancel</Button>
            <Button disabled={!files.length} variant="contained" onClick={() => {
                done(ids, files)
            }} startIcon={<Icon icon={challengeIcon} />}>Create challenges</Button>
        </>
    }
    ><>
            <Typography variant="body1">
                <p>Challenging a booking will allow you to obtain pole position from another campaign.  You must upload file(s) to
                    show acceptance of this campaign & its sites by the customer.</p>
            </Typography>
            <Card>
                <CardContent>
                    <FormLabel>Evidence</FormLabel>
                    <FileInput readOnly={false} name="files" label="Upload evidence" maxImageSize={900}
                        multiple={true} showPreview={true} value={files}
                        type="image" fileType="EV" allowDelete={true} onChange={changeFiles} />
                </CardContent>
            </Card>
        </>

    </ModalWindow>);
}

type PanelBookingUpdateTemp = {
    error:TweakPanelBookingError,
    request:TweakPanelBookingInput
};


const PanelBookingPane = () => {

    const { setValue, values, jobRef, updateInitialValues, readOnly, setTab } = useOutletContext<CampaignOutletContextValue>();
    const user = useUserContext();
    useEffect(() => {
        setTab('panel-bookings');
    }, []);
    const [showChallengeDialog, setShowChallengeDialog] = useState(false);
    const generateChallengeEmail = useCallback(() => {
        setShowChallengeDialog(true);
    }, []);
    const doneChallenge = () => {
        setShowChallengeDialog(false);
    }
    const [selectedRows, setSelectedRows] = useState({});
    const [waiting, setWaiting] = useState(false);
    const [doneRender, setDoneRender] = useState(false);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const daybook = useDaybookContext();
    const { loading, refetch, data } = useGetCampaignPanelBookingsQueryQuery({
        variables: { ref: jobRef },
        skip: true,
        fetchPolicy: 'network-only'
        /*onCompleted: (data) => {
            updateInitialValues(data.campaign.panelBookings, 'panelBookings');
        }*/
    });
    useEffect(() => {
        if (!doneRender && !loading && jobRef) {
            refetch({ ref: jobRef });
            setDoneRender(true);
        }
    }, [doneRender, loading, jobRef]);    
    const [tweakMutation] = useCampaignTweakPanelsMutationMutation();
    const [deleteMutation] = useCampaignDeletePanelsMutationMutation();
    //    const [challengeMutation] =useCampaignCreateChallengesMutation();
    const columns: FieldTableColumnProps<PanelBooking>[] = [
        {
            accessor: 'data.id',
            noSort: true,
            dataType: 'expander'
        },
        {
            accessor: 'selector',
            noSort: true,
            dataType: 'selector'
        },
        {
            header: 'Status',
            accessor: 'status',
            className: 'column-narrow',
            dataType: 'default',
            options: {
                renderViewer: ({ field, row }) => {
                    if (field.raw) return field.value;
                    if (field.value == 'Pencil') {
                        const icons: JSX.Element[] = [];
                        if (user.hasPermission('campaigns.challenge') || true) {
                            if (row.isPole) {
                                icons.push(<Tooltip title="In pole position for the entire duration"><Icon
                                    icon={poleIcon} /></Tooltip>);
                            }
                            if (row.isOutboundChallenging) {
                                icons.push(<Tooltip title="Challenging another booking for pole"><Icon icon={challengeIcon} /></Tooltip>);
                            }
                            return <>{icons} &nbsp;Pencil</>;
                        } else {
                            return <>{icons} &nbsp;Pencil</>;
                        }
                    } else {
                        return field.value;
                    }
                }
            }
        }, {
            header: 'Region',
            accessor: 'panel.site.boroughOrRegion',
            className: 'column-region',
            dataType: 'default'
        }, {
            header: 'Site Name',
            accessor: 'panel.site.name',
            dataType: 'default',
            options: {
                renderViewer: ({ field, row }) => {
                    return <Button component="a" href={row?.panel?.site?.externalLink} rel="noreferrer" target="_blank"><Web /> {field.value}
                    </Button>;
                }
            }
        }, {
            header: 'Poster Size',
            accessor: 'posterSize.name',
            dataType: 'default',
        }, {
            header: 'Site Structure',
            accessor: 'panel.site.siteStructure.name',
            dataType: 'default',
        }, {
            header: 'Weeks',
            accessor: 'weeks',
            dataType: 'default'
        }, {
            header: 'Date Up',
            accessor: 'first_week',
            dataType: 'date',
            readOnly: true,
        }, {
            header: 'Date Down',
            accessor: 'down_date',
            dataType: 'date',
            readOnly: true
        }, {
            header: 'Cost',
            accessor: 'campaignComponent.unit_value_local',
            dataType: 'currency',
            readOnly: true,
            options: {
                currency: {
                    accessor: 'campaignComponent.currency'
                }
            }
        }, {
            header: 'Distributor',
            accessor: 'panel.site.distributor.name',
            dataType: 'default'
        }, {
            header: 'Posters Received',
            accessor: 'is_received',
            dataType: 'boolean'
        },{
            header: 'Pro Photos',
            accessor: 'is_photo_paid',
            dataType: 'booleanCamToggle'
        },
        
    ];
    const exportColumns = [
        {
            header: 'Region',
            accessor: 'campaignComponent.region.name',
            dataType: 'default'
        }, {
            header: 'Borough',
            accessor: 'panel.site.borough.label',
            dataType: 'default'
        }, {
            header: 'Site',
            accessor: 'panel.site.name',
            exportAs: 'link',
            dataType: 'link',
            options: {
                getValue: ({ field, row }) => {
                    return field.value;
                },
                getLink({ row }) {
                    return row?.panel?.site?.externalLink;
                }
            }
        }, {
            header: 'Site Address',
            accessor: 'panel.site.address1',
            dataType: 'default',
        }, {
            header: 'Site Postcode',
            accessor: 'panel.site.postcode',
            dataType: 'default'
        }, {
            header: 'Site Code',
            accessor: 'panel.site.site_code',
            dataType: 'default'
        }, { 
            header: 'Site Structure',
            accessor: 'panel.site.siteStructure.name',
            dataType: 'default'
        }, {
            header: 'Poster Size',
            accessor: 'posterSize.name',
            dataType: 'default'
        }, {
            header: 'Poster Size Quantity',
            accessor: 'posterSize.effective_poster_count',
            dataType: 'number',
            options: {
                getValue: ({ row }) => {
                    return row.is_slot ? 1 : row.posterSize?.slot_poster_count;
                },
            },
            exportAs: 'number'
        }, {
            header: 'Cost',
            accessor: 'campaignComponent.unit_value_local',
            exportAs: 'currency',
            options: {
                currency: {
                    accessor: 'campaignComponent.currency'
                }
            },
            dataType: 'currency'
        }, {
            header: 'Display Weeks',
            accessor: 'weeks',
            exportAs: 'number',
            dataType: 'number'
        }, {
            header: 'Date Up',
            accessor: 'first_week',
            exportAs: 'date-uk',
            dataType: 'date',
            renderer: ({ field }) => {
                return isoDateToMMM(field.value);
            }
        }, {
            header: 'Date Down',
            accessor: 'down_date',
            exportAs: 'date-uk',
            dataType: 'date',
            renderer: ({ field }) => {
                return isoDateToMMM(field.value);
            }
        }, {
            header: 'Distributor',
            accessor: 'panel.site.distributor.name',
            dataType: 'default'
        }
    ];
    const [requiresOverride, setRequiresOverride] = useState<PanelBookingUpdateTemp[]|null>(null);
    const forceTweak = useCallback(async ()=>{
        if(waiting || !requiresOverride) return;
        setWaiting(true);
        enqueueSnackbar('Updating panel bookings', { key: 'PANEL_UPDATE' });
        try {
            const toUpdate = requiresOverride.map((v)=>{
                const newValue = {...v.request};
                newValue.ignore_loss_of_pole = true;
                return newValue;
            });
            const data = await tweakMutation({ variables: { tweaks: toUpdate } });
            const panelBookings = data.data?.campaignUpdatePanelBooking?.panelBookings;
            updateInitialValues(panelBookings, 'panelBookings');
            let seenError = false;
            const poleFailures:PanelBookingUpdateTemp[] = [];
            data?.data?.campaignUpdatePanelBooking?.tweakPanelBookingErrors?.forEach((ajaxError) => {
                seenError = true;
                closeSnackbar('PANEL_UPDATE');
                enqueueSnackbar(<span dangerouslySetInnerHTML={{ __html: ajaxError?.message??'An error has occured' }} />, { variant: 'error', key: 'PANEL_UPDATE2' });
            });
            if(poleFailures.length) {
                setRequiresOverride(poleFailures);
            }
            if (!seenError && !poleFailures.length) {
                // somewhat random decision to not show this for poleFailures?
                closeSnackbar('PANEL_UPDATE');
                enqueueSnackbar('Panel bookings updated successfully', { variant: 'success', key: 'PANEL_UPDATE2' });

            }
            setRequiresOverride(null);
            setWaiting(false);
        } catch (e) {
            closeSnackbar('PANEL_UPDATE2');
            enqueueSnackbar(e.message, { variant: 'error', key: 'PANEL_UPDATE3' });
            setRequiresOverride(null);
            setWaiting(false);
        }

    },[requiresOverride,waiting]);
    const tweakRow = useCallback(async (evt) => {
        if (evt) evt.preventDefault();
        const el = evt.target.closest('button');
        const duration = parseInt(el.getAttribute('data-dur'));
        const weekNum = parseInt(el.getAttribute('data-start'));
        const trimWeeks = parseInt(el.getAttribute('data-trim') ?? '0');
        const status = el.getAttribute('data-status');
        console.log('callfrom', el, el.data);
        if (waiting) return;
        const toExec:TweakPanelBookingInput[] = [];
        Object.entries(selectedRows).forEach(([k, value]) => {
            if (value) {
                toExec.push({ id: k, num_weeks: duration, week_number: weekNum, status: status, trim: trimWeeks });
            }
        });
        if (!toExec.length) return;
        setWaiting(true);
        enqueueSnackbar('Updating panel bookings', { key: 'PANEL_UPDATE' });
        try {
            const data = await tweakMutation({ variables: { tweaks: toExec } });
            console.log('DATA IS', data);
            const panelBookings = data?.data?.campaignUpdatePanelBooking?.panelBookings;
            updateInitialValues(panelBookings, 'panelBookings');
            //            setValue('panelBookings', panelBookings);
            let seenError = false;
            const poleFailures:PanelBookingUpdateTemp[] = [];
            data.data?.campaignUpdatePanelBooking?.tweakPanelBookingErrors?.forEach((ajaxError) => {
                seenError = true;
                if(ajaxError?.category=='POLE') {
                    // save the parameters sent to the backend so that the same request can be forced
                    const request = toExec.find((request)=>request.id==ajaxError.id);
                    if(!request) return;
                    poleFailures.push({error:ajaxError,request:request});
                    return;
                }
                closeSnackbar('PANEL_UPDATE');
                enqueueSnackbar(<span dangerouslySetInnerHTML={{ __html: ajaxError?.message??'An error has occured' }} />, { variant: 'error', key: 'PANEL_UPDATE2' });
            });
            if(poleFailures.length) {
                setRequiresOverride(poleFailures);
            }
            if (!seenError && !poleFailures.length) {
                // somewhat random decision to not show this for poleFailures?
                closeSnackbar('PANEL_UPDATE');
                enqueueSnackbar('Panel bookings updated successfully', { variant: 'success', key: 'PANEL_UPDATE2' });

            }
            setWaiting(false);
        } catch (e) {
            closeSnackbar('PANEL_UPDATE2');
            enqueueSnackbar(e.message, { variant: 'error', key: 'PANEL_UPDATE3' });
            setWaiting(false);
        }
    }, [waiting, selectedRows, tweakMutation, setValue]);
    const deleteRow = useCallback(async (evt) => {
        if (evt) evt.preventDefault();
        if (waiting) return;
        const toExec = [];
        Object.entries(selectedRows).forEach(([k, value]) => {
            if (value) {
                toExec.push(k);
            }
        });
        if (!toExec.length) return;
        setWaiting(true);

        try {
            const data = await deleteMutation({ variables: { deletes: toExec } });
            console.log('PBD', data);
            const panelBookings = data?.data?.campaignDeletePanelBooking?.panelBookings;
            setValue('panelBookings', panelBookings);
            let seenError = false;
            data?.data?.campaignDeletePanelBooking?.deletePanelBookingErrors?.forEach((ajaxError) => {
                seenError = true;
                enqueueSnackbar(<span dangerouslySetInnerHTML={{ __html: ajaxError?.message??'An error has occured' }} />, { variant: 'error', key: 'PANEL_DELETE1' })
                    ;
            });
            if (!seenError) {
                enqueueSnackbar('Panel booking(s) deleted successfully', { variant: 'error', key: 'PANEL_DELETE2' });
            }
            const newSelected = {};
            Object.entries(selectedRows).forEach(([k, v]) => {
                if (toExec.indexOf(k) >= 0) return;
                newSelected[k] = v;
            });
            setSelectedRows(newSelected);
            setWaiting(false);
        } catch (e) {
            enqueueSnackbar(e.message, { variant: 'error', key: 'PANEL_DELETE3' });
            setWaiting(false);
        }

    }, [deleteMutation, selectedRows, setValue, waiting]);

    if (waiting) return <WaitSpinner variant="overlay" />;

    // if no entries are selected then disable the buttons
    const noSel = !Object.entries(selectedRows).some(([, v]) => v);
    const toExec:string[] = [];
    Object.entries(selectedRows).forEach(([k, value]) => {
        if (value) {
            toExec.push(k);
        }
    });
    const summaries = {};
    let totalInhouse6040Count = 0;
    values?.panelBookings?.forEach((row) => {
        let region;
        if (!row) return;
        if (row.campaignComponent?.regionType?.name == 'London') {
            region = 'London';
        } else {
            region = row.campaignComponent?.region?.name;
        }
        if (!summaries[region]) { summaries[region] = {}; }
        const regionSummary = summaries[region];
        if (!row.is_slot) {
            if (regionSummary[row.posterSize?.name]) {
                regionSummary[row.posterSize?.name]++;
            } else {
                regionSummary[row.posterSize?.name] = 1;
            }
        } else {
            // get the BASE poster size name
            if (!regionSummary[row.posterSize?.slotPosterSize?.name]) {
                regionSummary[row.posterSize?.slotPosterSize?.name] = 0;
            }
            regionSummary[row.posterSize?.slotPosterSize?.name] += row.posterSize?.slot_poster_count;
        }
        if(row.panel?.site?.distributor?.is_inhouse == 1){
            totalInhouse6040Count += row.posterSize?.effective_poster_count || 0;
        }
    });
    console.log("Total Inhouse 60x40 Count:", totalInhouse6040Count);

    const summaryHtml =
        Object.keys(summaries).sort().map((regionName) => {
            const regionSummary = summaries[regionName];
            const content = Object.keys(regionSummary).sort().map((posterSize) => {
                const qty = regionSummary[posterSize];
                return <div key={posterSize}>{posterSize + ' : ' + qty}</div>;
            });
            return (<div key={regionName}><span
                className="campaign__panel-bookings__footer__summaries__region">{regionName}</span>
                {content}
            </div>);
        });

    const doChange = (evt) => {
        setValue(evt.target.name, evt.target.value);
    };
    const getRowClass = (data) => {
        return 'booking-status__bg__' + data?.status?.toLowerCase();
    }
    return (
        <><div className="is-scrollable">
            <FieldTable name="panelBookings" columns={columns}
                setSelectedRows={setSelectedRows}
                selectedRows={selectedRows}
                hasDelete={false}
                className="campaigns__panel-bookings"
                values={values.panelBookings}
                loading={loading}
                stickyFooter={true}
                setValue={setValue}
                getRowClass={getRowClass}
                readOnly={readOnly}
                exportColumns={exportColumns}
                footerClassName="campaign__panel-bookings__footer"
                footer={<><div className="campaign__panel-bookings__footer__actions">
                    {!readOnly &&
                        <ButtonGroup size="large">
                            <Button disabled={noSel} title="Move to previous week" data-start={-1} data-dur={0} onClick={tweakRow}
                            ><ChevronLeft /></Button>
                            <Button disabled={noSel} title="Move to next week" data-start={1} data-dur={0} onClick={tweakRow}
                            ><ChevronRight /></Button>
                            <Button disabled={noSel} title="Extend duration by 1 week" data-start={0} data-dur={1} onClick={tweakRow}
                            ><Expand /></Button>
                            <Button disabled={noSel} title="Shorten duration by 1 week" data-start={0} data-dur={-1} onClick={tweakRow}
                            ><Compress /></Button>
                            <Button disabled={noSel} variant="contained" color="success" data-start={0} data-dur={0} data-status="Booked" title="Confirm bookings" onClick={tweakRow}>
                                <ThumbUpSharp /></Button>
                            <Tooltip title="Trim dates to weeks where 1st option has been won.">
                                <Button disabled={noSel} variant="contained" color="warning" title="Trim bookings" data-trim={1} data-start={0} data-dur={0} onClick={tweakRow}>
                                    <Icon icon="fluent-mdl2:trim" fontSize={20} />
                                </Button>
                            </Tooltip>
                            <Button disabled={noSel} variant="contained" color="warning" title="Delete bookings" onClick={deleteRow}>
                                <Delete />
                            </Button>
                            {daybook?.challengeEnabled && (user.hasPermission('campaigns.challenge') || true) &&
                                <Button disabled={noSel} variant="contained" title="Challenge" onClick={generateChallengeEmail}>
                                    <Icon icon="fluent:mail-list-20-regular" fontSize={20} />
                                </Button>
                            }
                            <Button disabled={noSel} color="info" data-start={0} data-dur={0} data-status="Pencil" title="Revert to pencil booking" onClick={tweakRow}>
                                <Icon icon={pencilIcon} />
                            </Button>
                        </ButtonGroup>
                    }
                </div>
                    <div className="campaign__panel-bookings__footer__summaries">{summaryHtml}</div>
                </>}
            >{({ name, row }) => {
                return <TextField sx={{ marginTop: 0, marginBottom: 0 }} disabled={readOnly} multiline={true} name={name + '.instructions'} onChange={doChange} fullWidth={true} value={row.instructions} label="Any special instructions" />
            }}
            </FieldTable></div>
            <ChallengeEmailDialog
                ourContact={values.ourContact}
                isShown={showChallengeDialog} done={doneChallenge} reference={values.job_reference}
                selectedBookings={selectedRows} />
    <ModalWindow isOpen={!!requiresOverride} onClose={()=>setRequiresOverride(null)} header={
            <h3>Warning</h3>
        } actions={
            <>
            <Button size="large" color="warning" onClick={()=>setRequiresOverride(null)} startIcon={<Cancel/>}>Cancel</Button>
            <Button size="large" color="primary" onClick={forceTweak} startIcon={<Save/>}>Save, losing pole</Button>
            </>
        }>
          <Typography>The below bookings would lose 1st option for 1 or more weeks by this action<br/>
          <ul>
            {requiresOverride?.map((override)=>(
                <li key={override.error.id}>{override?.error.siteName}</li>
            ))}
          </ul>
          Use <Typography color="primary" style={{display:'inline-block'}}><Icon icon="fluent-mdl2:trim" fontSize={20} /></Typography> to trim booking to weeks where 1st option has been won
          </Typography>
        </ModalWindow>
        </>
    );
};


PanelBookingPane.propTypes = {
    values: PropTypes.array,
    setValue: PropTypes.func
};

export default PanelBookingPane;
